import type { RaRecord } from "react-admin";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { formatBookingHallNames } from "./booking-hall-names";
import { formatListDate } from "./date-format";
import {
  formatNarrationIts,
  formatNarrationRupee,
  formatNarrationTransactionId,
} from "./receipt-narration-format";

dayjs.extend(utc);

export type RentHallBookingNarration = {
  date?: string | Date | null;
  hall?: { name?: string | null } | null;
  bookingPurpose?: { name?: string | null } | null;
};

export type RentBookingNarrationInput = {
  organiserIts?: unknown;
  organiser?: unknown;
  amount?: unknown;
  mode?: unknown;
  ref?: unknown;
  transactionId?: unknown;
  paymentRef?: unknown;
  hallNames?: string;
  purposeName?: unknown;
  bookingDate?: unknown;
  booking?: {
    purposeName?: string | null;
    bookingDate?: string | null;
    hallBookings?: RentHallBookingNarration[];
  } | null;
};

function resolveHallNames(input: RentBookingNarrationInput, rec?: RaRecord): string {
  const explicit = String(input.hallNames ?? "").trim();
  if (explicit) return explicit;
  if (rec) return formatBookingHallNames(rec);
  const hallsRaw = input.booking?.hallBookings;
  const names = [
    ...new Set(
      (hallsRaw ?? []).map((hb) => hb.hall?.name?.trim()).filter((n): n is string => Boolean(n))
    ),
  ];
  return names.join(", ");
}

function resolvePurpose(input: RentBookingNarrationInput): string {
  const top = String(input.purposeName ?? input.booking?.purposeName ?? "").trim();
  if (top) return top;
  const names = [
    ...new Set(
      (input.booking?.hallBookings ?? [])
        .map((hb) => hb.bookingPurpose?.name?.trim())
        .filter((n): n is string => Boolean(n))
    ),
  ];
  return names.join(", ");
}

function resolveBookingDate(input: RentBookingNarrationInput): string {
  const preset = String(input.bookingDate ?? input.booking?.bookingDate ?? "").trim();
  if (preset) {
    const d = dayjs(preset);
    return d.isValid() ? formatListDate(preset, { utc: true }) : preset;
  }
  const dates = (input.booking?.hallBookings ?? [])
    .map((hb) => hb.date)
    .filter((d) => d != null && d !== "")
    .map((d) => dayjs.utc(d as string | Date))
    .filter((d) => d.isValid())
    .sort((a, b) => a.valueOf() - b.valueOf());
  const first = dates[0];
  return first ? formatListDate(first.toISOString(), { utc: true }) : "";
}

export type RentBookingReceiptNarrationOptions = {
  includeItsNo?: boolean;
  includeName?: boolean;
  includeAmount?: boolean;
  includePaymentMode?: boolean;
  includeTransactionId?: boolean;
  includeHall?: boolean;
  includePurpose?: boolean;
  includeBookingDate?: boolean;
};

/** ITS, name, amount, payment mode, txn id, hall, purpose, booking date — Tally export (not printed on receipt). */
export function formatRentBookingReceiptNarration(
  input: RentBookingNarrationInput,
  rec?: RaRecord,
  options?: RentBookingReceiptNarrationOptions
): string {
  const includeItsNo = options?.includeItsNo ?? true;
  const includeName = options?.includeName ?? true;
  const includeAmount = options?.includeAmount ?? true;
  const includePaymentMode = options?.includePaymentMode ?? true;
  const includeTransactionId = options?.includeTransactionId ?? true;
  const includeHall = options?.includeHall ?? true;
  const includePurpose = options?.includePurpose ?? true;
  const includeBookingDate = options?.includeBookingDate ?? true;

  const itsPart = includeItsNo ? formatNarrationIts(input.organiserIts) : "";
  const name = includeName ? String(input.organiser ?? "").trim() : "";
  const amount = includeAmount ? formatNarrationRupee(input.amount ?? rec?.amount) : "";
  const paymentMode = includePaymentMode
    ? String(input.mode ?? "")
        .trim()
        .toUpperCase()
    : "";
  const txnPart = includeTransactionId
    ? formatNarrationTransactionId(input.transactionId ?? input.ref ?? input.paymentRef)
    : "";
  const hallName = includeHall ? resolveHallNames(input, rec) : "";
  const purpose = includePurpose ? resolvePurpose(input) : "";
  const bookingDate = includeBookingDate ? resolveBookingDate(input) : "";

  return [itsPart, name, amount, paymentMode, txnPart, hallName, purpose, bookingDate]
    .filter((part) => part !== "")
    .join(", ");
}

export function formatRentBookingReceiptNarrationFromRecord(
  rec: RaRecord,
  options?: RentBookingReceiptNarrationOptions
): string {
  return formatRentBookingReceiptNarration(
    {
      organiserIts: rec.organiserIts,
      organiser: rec.organiser,
      amount: rec.amount,
      mode: rec.mode,
      ref: rec.ref,
      transactionId: rec.transactionId,
      paymentRef: rec.paymentRef,
      purposeName: (rec.booking as { purposeName?: string } | undefined)?.purposeName,
      bookingDate: (rec.booking as { bookingDate?: string } | undefined)?.bookingDate,
      booking: rec.booking as RentBookingNarrationInput["booking"],
    },
    rec,
    options
  );
}
